// The one call being worked from the LTR screens, and the in-call pill's view
// of it. Framework-free and module-scoped so the pill survives navigating
// between the dashboard and a case; mirrored to sessionStorage so a reload
// mid-call does not lose it.
//
// phase: "dialling" → "connected" → "ended". SparkTG only reports ring and
// hang-up, so "connected" is set by the doctor from the pill; "ended" comes
// from the pill's hang-up button or a hang-up event on the call stream.

const STORAGE_KEY = "ltr.callSession";

let session = load();
const listeners = new Set();
// Requests from the pill ("open the create-appointment popup") for whichever
// case page is mounted. Held until a page for that case consumes them.
let pendingAction = null;
const actionListeners = new Set();

function load() {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
}

function persist() {
  try {
    if (session) window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    else window.sessionStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    // Private browsing can make sessionStorage throw; the pill still works
    // for this page view.
  }
}

function emit() {
  persist();
  listeners.forEach((fn) => {
    try {
      fn(session);
    } catch (err) {
      console.error("call session listener threw", err);
    }
  });
}

const callSession = {
  get() {
    return session;
  },

  subscribe(fn) {
    listeners.add(fn);
    return () => listeners.delete(fn);
  },

  // A call has just been placed for a case. `ltrCall` is the call the LTR
  // service opened for it.
  start({ caseId, ltrId, patientId, patientName, ltrCall, caseRecord }) {
    session = {
      caseId,
      ltrId,
      patientId,
      patientName,
      callId: ltrCall.callId,
      phase: "dialling",
      startedAt: ltrCall.startedAt || new Date().toISOString(),
      connectedAt: null,
      endedAt: null,
      followUpDone: false,
      caseRecord: caseRecord || null,
    };
    emit();
  },

  update(patch) {
    if (!session) return;
    session = { ...session, ...patch };
    emit();
  },

  // The case page keeps the pill's copy of the case fresh, so the step
  // states (appointment booked, Rx linked, actions set) follow the server.
  syncCase(caseRecord) {
    if (!session || !caseRecord || caseRecord.id !== session.caseId) return;
    session = { ...session, caseRecord };
    emit();
  },

  clear() {
    session = null;
    emit();
  },

  // ---- pill → page requests ---------------------------------------------

  requestAction(kind) {
    if (!session) return;
    pendingAction = { kind, caseId: session.caseId, callId: session.callId };
    actionListeners.forEach((fn) => fn(pendingAction));
  },

  // A mounted case page claims a pending request for itself.
  takeAction(caseId) {
    if (!pendingAction || pendingAction.caseId !== caseId) return null;
    const action = pendingAction;
    pendingAction = null;
    return action;
  },

  onAction(fn) {
    actionListeners.add(fn);
    return () => actionListeners.delete(fn);
  },
};

// The call this session is about, as the LTR service last reported it.
export function sessionCall(s) {
  const calls = s?.caseRecord?.calls || [];
  return calls.find((c) => c.callId === s.callId) || null;
}

// The appointment converted from this session's call, if any.
export function sessionAppointment(s) {
  const appts = s?.caseRecord?.appointments || [];
  return appts.find((a) => a.callId === s.callId) || null;
}

export default callSession;
